import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useState, } from 'react';
import { AuthClient } from '../client/AuthClient';
import { AuthError, } from '../types/auth';
import { withAuthErrorHandling, withAuthErrorHandlingAndRethrow, withAuthErrorHandlingNullable, } from '../utils/error-handler';
const AuthContext = createContext(null);
/**
 * React context provider for authentication
 */
export const AuthProvider = ({ children, config, onAuthStateChange, onError, onChallenge, }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [challenge, setChallenge] = useState(null);
    // Auth state change handler
    const handleInternalAuthStateChange = useCallback((authenticated, reason) => {
        if (authenticated) {
            setIsAuthenticated(true);
            setError(null);
            setChallenge(null);
        }
        else {
            setIsAuthenticated(false);
            setUser(null);
            setChallenge(null);
            // Set appropriate error message based on logout reason
            if (reason === 'token_revoked') {
                setError(new AuthError('TOKEN_EXPIRED', 'Your session has been ended by an administrator. Please log in again.'));
            }
            else if (reason === 'session_expired') {
                setError(new AuthError('TOKEN_EXPIRED', 'Your session has expired. Please log in again.'));
            }
        }
        // Call the external callback if provided (only pass authenticated state)
        onAuthStateChange?.(authenticated);
    }, [onAuthStateChange]);
    const [authClient] = useState(() => {
        const client = new AuthClient({
            ...config,
            onAuthStateChange: handleInternalAuthStateChange,
        });
        return client;
    });
    const handleError = useCallback((authError) => {
        setError(authError);
        onError?.(authError);
    }, [onError]);
    const handleChallenge = useCallback((authChallenge) => {
        setChallenge(authChallenge);
        onChallenge?.(authChallenge);
    }, [onChallenge]);
    const clearError = useCallback(() => {
        setError(null);
    }, []);
    const clearChallenge = useCallback(() => {
        setChallenge(null);
    }, []);
    // Initialize authentication state
    useEffect(() => {
        const initializeAuth = async () => {
            await withAuthErrorHandling(async () => {
                const authenticated = await authClient.isAuthenticated();
                setIsAuthenticated(authenticated);
                if (authenticated) {
                    const userInfo = await authClient.getUserInfo();
                    setUser(userInfo);
                }
            }, {
                errorMessage: 'Failed to initialize auth',
                onError: handleError,
                setLoading: setIsLoading,
            });
        };
        initializeAuth();
    }, [authClient, handleError]);
    // Notify parent of auth state changes
    useEffect(() => {
        onAuthStateChange?.(isAuthenticated);
    }, [isAuthenticated, onAuthStateChange]);
    const login = useCallback(async (credentials) => {
        try {
            setIsLoading(true);
            clearError();
            clearChallenge();
            const response = await authClient.login(credentials);
            if (response.success) {
                // Successful authentication
                const userInfo = await authClient.getUserInfo();
                setUser(userInfo);
                setIsAuthenticated(true);
            }
            else {
                // Authentication challenge
                handleChallenge(response.challenge);
            }
        }
        catch (err) {
            const authError = err instanceof AuthError
                ? err
                : new AuthError('UNKNOWN_ERROR', 'Login failed', err);
            handleError(authError);
            throw authError;
        }
        finally {
            setIsLoading(false);
        }
    }, [authClient, handleError, handleChallenge, clearError, clearChallenge]);
    const logout = useCallback(async () => {
        await withAuthErrorHandling(async () => {
            await authClient.logout();
            setUser(null);
            setIsAuthenticated(false);
            setError(null);
        }, {
            errorMessage: 'Logout failed',
            onError: handleError,
            setLoading: setIsLoading,
        });
    }, [authClient, handleError]);
    const globalSignOut = useCallback(async () => {
        await withAuthErrorHandling(async () => {
            await authClient.globalSignOut();
            setUser(null);
            setIsAuthenticated(false);
            setError(null);
        }, {
            errorMessage: 'Global sign out failed',
            onError: handleError,
            setLoading: setIsLoading,
        });
    }, [authClient, handleError]);
    const refreshToken = useCallback(async () => {
        await withAuthErrorHandlingAndRethrow(async () => {
            await authClient.refreshToken();
            // Refresh user info after token refresh
            const userInfo = await authClient.getUserInfo();
            setUser(userInfo);
        }, 'Token refresh failed', (authError) => {
            handleError(authError);
            // If refresh fails, user is likely no longer authenticated
            setIsAuthenticated(false);
            setUser(null);
        });
    }, [authClient, handleError]);
    const getAccessToken = useCallback(async () => {
        return withAuthErrorHandlingNullable(() => authClient.getAccessToken(), 'Failed to get access token', handleError);
    }, [authClient, handleError]);
    const isTokenExpired = useCallback(async (token, tokenUse = 'access') => {
        try {
            return await authClient.isTokenExpired(token, tokenUse);
        }
        catch {
            // If validation fails, consider token expired
            return true;
        }
    }, [authClient]);
    const changePassword = useCallback(async (oldPassword, newPassword) => {
        await withAuthErrorHandlingAndRethrow(() => authClient.changePassword({ oldPassword, newPassword }), 'Password change failed', handleError);
    }, [authClient, handleError]);
    const forgotPassword = useCallback(async (username) => {
        await withAuthErrorHandlingAndRethrow(() => authClient.forgotPassword({ username }), 'Forgot password failed', handleError);
    }, [authClient, handleError]);
    const confirmPassword = useCallback(async (username, code, newPassword) => {
        await withAuthErrorHandlingAndRethrow(() => authClient.confirmPassword({
            username,
            confirmationCode: code,
            newPassword,
        }), 'Password confirmation failed', handleError);
    }, [authClient, handleError]);
    const completeNewPassword = useCallback(async (username, newPassword) => {
        await withAuthErrorHandlingAndRethrow(async () => {
            await authClient.completeNewPassword({ username, newPassword });
            const userInfo = await authClient.getUserInfo();
            setUser(userInfo);
            setIsAuthenticated(true);
            clearChallenge(); // Clear the challenge after successful completion
        }, 'New password completion failed', handleError, setIsLoading, clearError);
    }, [authClient, handleError, clearChallenge]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            authClient.dispose();
        };
    }, [authClient]);
    const contextValue = {
        user,
        isAuthenticated,
        isLoading,
        error,
        challenge,
        login,
        logout,
        globalSignOut,
        refreshToken,
        getAccessToken,
        isTokenExpired,
        clearError,
        clearChallenge,
        changePassword,
        forgotPassword,
        confirmPassword,
        completeNewPassword,
    };
    return (_jsx(AuthContext.Provider, { value: contextValue, children: children }));
};
/**
 * Hook to access authentication context
 */
export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};
//# sourceMappingURL=AuthProvider.js.map